import React from 'react';

interface InlineImageProps {
  src: string;
  alt?: string;
  className?: string;
  width?: number | string;
  height?: number | string;
}

const InlineImage: React.FC<InlineImageProps> = ({ src, alt, className = '', width, height }) => {
  const [hasError, setHasError] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [attemptedFetch, setAttemptedFetch] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);

  const currentSrc = blobUrl || src;

  React.useEffect(() => {
    // Cleanup blob URL on unmount or when src changes
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const onError = async (e?: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('InlineImage load error:', src, e);

    // If we haven't tried fetching as a blob, try to fetch it (may bypass some hotlink/CORS behaviours)
    if (!attemptedFetch) {
      setAttemptedFetch(true);
      try {
        const res = await fetch(src, { mode: 'cors' });
        if (res.ok) {
          const blob = await res.blob();
          const objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
          setHasError(false);
          setIsLoaded(false);
          return; // We'll let the image load from objectUrl
        } else {
          console.debug('InlineImage fetch returned non-ok status', res.status, src);
        }
      } catch (fetchErr) {
        console.debug('InlineImage fetch-as-blob failed', fetchErr);
      }
    }

    setHasError(true);
  };

  const onLoad = () => setIsLoaded(true);

  const onRetry = () => {
    // Reset states and force a reload (cache-bust by toggling key)
    setHasError(false);
    setIsLoaded(false);
    setAttemptedFetch(false);
    setBlobUrl(null);
    setReloadKey(k => k + 1);
  };

  return (
    <div className={`inline-block align-middle ${className}`} style={{ width, height }}>
      {!isLoaded && !hasError && (
        <div className="bg-gray-100 animate-pulse rounded-md w-full h-full flex items-center justify-center">
          <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 11l2 2 4-4 6 6" />
          </svg>
        </div>
      )}

      {hasError ? (
        <div className="bg-gray-50 border border-red-200 rounded-md text-xs text-red-500 px-2 py-1 break-words">
          <div>Imagen no disponible</div>
          <div className="mt-1 flex gap-2">
            <a href={src} target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline underline-offset-2 hover:text-emerald-700 break-all">Abrir imagen</a>
            <button onClick={onRetry} className="text-emerald-600 underline underline-offset-2 hover:text-emerald-700">Reintentar</button>
          </div>
          {/* Mostrar URL para facilitar diagnóstico */}
          <div className="text-ellipsis break-all mt-1 text-xs text-gray-500">{src}</div>
        </div>
      ) : (
        <img
          key={`img-${reloadKey}-${String(!!blobUrl)}`}
          src={currentSrc}
          alt={alt || 'image'}
          onError={onError}
          onLoad={onLoad}
          className={`block max-w-full max-h-[400px] object-contain rounded-md ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
        />
      )}
    </div>
  );
};

export default InlineImage;
